import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { cleanNotifications, showNotification } from "@mantine/notifications";
import dayjs from "dayjs";
import pick from "lodash/pick";
import {
    CampaignInquiriesQuery,
    CampaignInquiryQuery,
    CreateCampaignInquiryInput,
} from "src/graphql/generated/graphql";
import { createCampaignInquiryMutation } from "src/graphql/operations/campaignInquiries/mutations";
import { campaignInquiriesQuery, campaignInquiryQuery } from "src/graphql/operations/campaignInquiries/query";
import { graphqlFetch } from "src/graphql/utils/graphqlFetch";
import { GraphQLRequestError } from "src/graphql/utils/utils";
import { TranslationKeys } from "src/types/i18next";

import { handleError, toSupportCode } from "../utils/error";
import { useAuthContext } from "./auth";

export const useCampaignInquiries = () => {
    const { client } = useAuthContext();

    const [campaignInquiries, setCampaignInquiries] = useState<CampaignInquiriesQuery["campaignInquiries"]["items"]>(
        []
    );
    const [isLoadingCampaignInquiries, setIsLoadingCampaignInquiries] = useState(false);

    const fetchCampaignInquiries = useCallback(
        async (nextToken?: string) => {
            setIsLoadingCampaignInquiries(true);

            let hasNext = false;
            try {
                const response = await graphqlFetch(campaignInquiriesQuery, { client: client?.id, nextToken });

                setCampaignInquiries((campaignInquiries) => [
                    ...campaignInquiries,
                    ...(response.campaignInquiries?.items || []),
                ]);

                if (response.campaignInquiries?.nextToken) {
                    hasNext = true;
                    fetchCampaignInquiries(response.campaignInquiries.nextToken);
                }
            } catch (err) {
                handleError(err, { context: { clientId: client?.id, nextToken } });
            } finally {
                if (!hasNext) {
                    setIsLoadingCampaignInquiries(false);
                }
            }
        },
        [client]
    );

    useEffect(() => {
        if (!client?.id) {
            return;
        }

        setCampaignInquiries([]);
        fetchCampaignInquiries();
    }, [fetchCampaignInquiries, client]);

    return {
        campaignInquiries,
        campaignInquiriesTotal: campaignInquiries?.length,
        isLoadingCampaignInquiries,
        reloadCampaignInquiries() {
            setCampaignInquiries([]);
            fetchCampaignInquiries();
        },
    };
};

export type CampaignInquiryQueryDataType = CampaignInquiryQuery["campaignInquiry"];

export const useCampaignInquiry = (campaignInquiryId: string) => {
    const [campaignInquiry, setCampaignInquiry] = useState<CampaignInquiryQueryDataType>(null);
    const [isLoadingCampaignInquiry, setIsLoadingCampaignInquiry] = useState(!!campaignInquiryId);

    const fetchCampaignInquiry = useCallback(async (campaignInquiryId: string) => {
        setIsLoadingCampaignInquiry(true);

        try {
            const response = await graphqlFetch(campaignInquiryQuery, { campaignInquiryId });

            setCampaignInquiry(response.campaignInquiry);
        } catch (err) {
            handleError(err, { context: { campaignInquiryId } });
        } finally {
            setIsLoadingCampaignInquiry(false);
        }
    }, []);

    useEffect(() => {
        if (!campaignInquiryId) {
            return;
        }

        fetchCampaignInquiry(campaignInquiryId);
    }, [campaignInquiryId, fetchCampaignInquiry]);

    return {
        campaignInquiry,
        isLoadingCampaignInquiry,
        reloadCampaignInquiry() {
            fetchCampaignInquiry(campaignInquiryId);
        },
    };
};

class EmptyCreateInquiryResponseError extends Error {
    name = "EmptyCreateInquiryResponseError";

    constructor() {
        super("createCampaignInquiry resolved without an inquiry");
    }
}

// Which message a failed save shows. The server's own words are not shown to the client; we
// recognise the few failures the client can act on and give every other failure a support code.
// The instanceof guard is load-bearing — this used to read `err.errors`, which
// GraphQLRequestError does not have, so the catch block itself threw (TTV2-1659).
export const classifyCreateInquiryError = (err: unknown): { key: TranslationKeys; params?: { code: string } } => {
    if (err instanceof EmptyCreateInquiryResponseError) {
        return { key: "campaigninquiry_saving_failed_code" };
    }

    // Only a rejected fetch (a TypeError in every browser) is known to be a connection problem —
    // and the same failure while Cognito refreshes the token, which amazon-cognito-identity-js
    // rethrows as Error('Network error') with `code === "NetworkError"` (keyed on the code, not the
    // message). Anything else that is not a GraphQL error — an unparseable body, a thrown string
    // such as Amplify's 'No current user' — is unknown, so it gets a support code, not a network claim.
    if (!(err instanceof GraphQLRequestError)) {
        return err instanceof TypeError || (err as { code?: unknown } | null | undefined)?.code === "NetworkError"
            ? { key: "campaigninquiry_saving_failed_network" }
            : { key: "campaigninquiry_saving_failed_code" };
    }

    const errors = err.errorsNormalized;

    // The questionnaire cap is the common one: copying a campaign prefills every question from the
    // source, which can exceed the client's limit (TTV2-1659).
    if (errors.some((error) => error.errorType === "LimitExceeded")) {
        return { key: "campaigninquiry_saving_failed_limit_exceeded" };
    }

    // client-api's getClient resolver refuses a user who is not in the company's group with
    // util.error('Forbidden', 'ClientAccessDenied'); matched on the message, which TTV2-1919 kept.
    if (errors.some((error) => error.message === "Forbidden" || error.errorType === "Unauthorized")) {
        return { key: "campaigninquiry_saving_failed_forbidden" };
    }

    // getImages: an image id that is missing or has no `:FILE:` row, typically the leftover of an
    // upload that failed earlier (`Invalid image ID: 'undefined'`).
    if (errors.some((error) => /^Invalid image ID: '/.test(error.message))) {
        return { key: "campaigninquiry_saving_failed_invalid_image" };
    }

    return {
        key: "campaigninquiry_saving_failed_code",
        params: err.requestId ? { code: toSupportCode(err.requestId) } : undefined,
    };
};

export const useCreateCampaignInquiry = () => {
    const { t } = useTranslation(["common"]);
    const [isCreatingCampaignInquiry, setIsCreatingCampaignInquiry] = useState(false);

    const createCampaignInquiry = useCallback(
        async (campaignInquiry: CreateCampaignInquiryInput) => {
            setIsCreatingCampaignInquiry(true);
            showNotification({
                color: "blue",
                message: t("campaigninquiry_saving"),
                loading: true,
            });

            try {
                campaignInquiry.products = (campaignInquiry?.products || []).map((product) => ({
                    ...product,
                    category: {
                        key: product?.category?.key,
                    },
                }));

                if (campaignInquiry?.settings?.openAt) {
                    campaignInquiry.settings.openAt = dayjs(campaignInquiry.settings.openAt).valueOf();
                }

                // An entry without a file id would make getImages refuse the whole save
                // (`Invalid image ID: 'undefined'`). An old-bundle draft can still hold one — a hidden
                // `{ file: undefined }` or `{ file: {} }` from an upload that failed — so drop it here.
                campaignInquiry.images = (campaignInquiry.images || [])
                    .filter((image) => image?.file?.id)
                    .map((image) => ({
                        ...image,
                        file: pick(image?.file, ["id", "objectKey"]),
                    }));

                const response = await graphqlFetch(createCampaignInquiryMutation, { campaignInquiry });

                // AppSync can resolve with no inquiry and no error; without this the caller gets
                // `undefined` and the client no message at all.
                if (!response.createCampaignInquiry) {
                    throw new EmptyCreateInquiryResponseError();
                }

                // Notifications are global and outlive the route change that follows a save: drop
                // the blue "saving" toast and any red failure toast a sticky earlier attempt left.
                cleanNotifications();

                return response.createCampaignInquiry;
            } catch (err) {
                const { key, params } = classifyCreateInquiryError(err);

                handleError(err, {
                    message: { key, namespace: "common", params },
                    // These two messages end in a code the client has to read out or copy; a toast
                    // that closes by itself takes it away. The others have nothing to quote.
                    autoClose: !(
                        key === "campaigninquiry_saving_failed_code" || key === "campaigninquiry_saving_failed_network"
                    ),
                    // No request id to quote (a failure that never reached the server, or an answer
                    // without the header): the Sentry event id is the only code we can give.
                    supportCodeFromEvent:
                        key === "campaigninquiry_saving_failed_network" ||
                        (key === "campaigninquiry_saving_failed_code" && !params?.code),
                });
            } finally {
                setIsCreatingCampaignInquiry(false);
            }
        },
        [t]
    );

    return {
        isCreatingCampaignInquiry,
        createCampaignInquiry,
    };
};
